import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

import type { AxiosResponse } from "axios";
import axios from "axios";

import type { DashboardsByCategory, SisenseDashboardList } from "~/src/types";

interface InsightsHubCategoriesState {
  data: DashboardsByCategory[];
  loading: boolean;
  error: string | null;
}

interface InsightsHubDashboardsState {
  data: SisenseDashboardList[];
  loading: boolean;
  error: string | null;
}

interface InsightsHubContextType {
  loadInsightsHubCategories: (queryParameters?: string) => void;
  insightsHubCategories: InsightsHubCategoriesState;
  getDashboards: () => void;
  insightsHubDashboards: InsightsHubDashboardsState;
  updateDashboard: (updatedDashboard: SisenseDashboardList) => void;
}

const InsightsHubContext = createContext<InsightsHubContextType | undefined>(
  undefined,
);

interface InsightsHubProviderProps {
  children: ReactNode;
}

export const InsightsHubProvider = ({ children }: InsightsHubProviderProps) => {
  const [insightsHubCategories, setInsightsHubCategories] =
    useState<InsightsHubCategoriesState>({
      data: [],
      loading: true,
      error: null,
    });

  const [insightsHubDashboards, setInsightsHubDashboards] =
    useState<InsightsHubDashboardsState>({
      data: [],
      loading: true,
      error: null,
    });

  const loadInsightsHubCategories = useCallback(() => {
    axios
      .get("sisense/dashboards/dashboards_by_category?embedded=true")
      .then((res) => {
        setInsightsHubCategories({
          data: res.data.result.filter(
            ({ dashboards }: { dashboards: SisenseDashboardList[] }) =>
              dashboards.some(({ embedded }: SisenseDashboardList) => embedded),
          ),
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        console.error("Error fetching dashboards by category:", err);
        setInsightsHubCategories({
          data: [],
          loading: false,
          error: "Failed to load dashboards by category",
        });
      });
  }, []);

  const getDashboards = () => {
    setInsightsHubDashboards((prev) => {
      return { ...prev, loading: true };
    });

    axios
      .get("/sisense/dashboards")
      .then((res: AxiosResponse<{ result: SisenseDashboardList[] }>) => {
        setInsightsHubDashboards({
          data: res.data.result.filter(
            (o) => o.embedded || o.type === "Ad Hoc Analysis",
          ),
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        console.error("Error fetching dashboards:", err);
        let errorMessage = "Failed to load dashboards";
        if (
          err.response?.data?.result?.error ===
          "User does not have a Sisense account or is not authorized"
        ) {
          console.error("User does not have access to Sisense:", err);
          errorMessage =
            "Your account does not have access to Insights Hub. Please contact your administrator.";
        } else {
          console.error("Error fetching dashboards:", err);
        }

        setInsightsHubDashboards({
          data: [],
          loading: false,
          error: errorMessage,
        });
      });
  };

  const updateDashboard = (updatedDashboard: SisenseDashboardList) => {
    setInsightsHubDashboards((prev) => {
      return {
        ...prev,
        data: prev.data.map((dashboard) =>
          dashboard.uuid === updatedDashboard.uuid
            ? updatedDashboard
            : dashboard,
        ),
      };
    });

    setInsightsHubCategories((prev) => {
      return {
        ...prev,
        data: prev.data.map((category) => {
          return {
            ...category,
            dashboards: (category.dashboards || []).map((dashboard) =>
              dashboard.uuid === updatedDashboard.uuid
                ? updatedDashboard
                : dashboard,
            ),
          };
        }),
      };
    });
  };

  const value: InsightsHubContextType = {
    loadInsightsHubCategories,
    insightsHubCategories,
    getDashboards,
    insightsHubDashboards,
    updateDashboard,
  };

  return (
    <InsightsHubContext.Provider value={value}>
      {children}
    </InsightsHubContext.Provider>
  );
};

export const useInsightsHubContext = (): InsightsHubContextType => {
  const context = useContext(InsightsHubContext);
  if (context === undefined) {
    throw new Error(
      "useInsightsHubContext must be used within a InsightsHubProvider",
    );
  }
  return context;
};
